import { Component, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';

import { Loading } from 'src/shared/components/loading/loading';
import { Client } from 'src/providers/client/client';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { Me } from 'src/providers/me/me';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

import { ClientSettingsModel } from 'src/models/client.model';

import validationUtils from 'src/shared/util/validation-utils';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { SuccessPage } from 'src/shared/pages/success/success';
import { parsePhoneNumber } from 'src/tidy-ui-components/components/form/input-international-phone/international-number.utils';

@Component({
    templateUrl: 'demo.html',
    standalone: false
})

export class DemoPage implements OnInit {

  autocompleteItems: any[] = [
    { value: 'option1', display: 'Autocomplete option 1' },
    { value: 'option2', display: 'Autocomplete option 2' },
    { value: 'option3', display: 'Autocomplete option 3' },
  ];
  boundSave = this.save.bind(this);
  checkboxItems: any[] = [
    { value: 'check1', viewValue: 'Checkbox option 1', checked: true },
    { value: 'check2', viewValue: 'Checkbox option 2', checked: false },
  ];
  countryCode: string;
  dialogParams: any;
  errorMessage: string;
  form: UntypedFormGroup;
  isRightSideContent = true;
  loaded: boolean;
  radioItems: any[] = [
    { value: 'radio1', viewValue: 'Radio option 1' },
    { value: 'radio2', viewValue: 'Radio option 2' },
    { value: 'radio3', viewValue: 'Radio option 3' },
  ];
  selectItems: any[] = [
    { value: 'select1', viewValue: 'Select option 1' },
    { value: 'select2', viewValue: 'Select option 2' },
    { value: 'select3', viewValue: 'Select option 3' },
    { value: 'select4', viewValue: 'Select option 4' },
    { value: 'select5', viewValue: 'Select option 5' },
    { value: 'select6', viewValue: 'Select option 6' },
    { value: 'select7', viewValue: 'Select option 7' },
    { value: 'select8', viewValue: 'Select option 8' },
    { value: 'select9', viewValue: 'Select option 9' },
    { value: 'select10', viewValue: 'Select option 10' },
  ];
  settings: ClientSettingsModel;
  showCompanyName: boolean;
  submitted: boolean;

constructor(
    private client: Client,
    private fb: UntypedFormBuilder,
    private me: Me,
    private storage: TidyStorage,
    private navCtrl: CustomNavController,
    private rightSidePanelService: RightSidePanelService,
  ) {
    this.form = this.fb.group({
      autocomplete: [''],
      autocompleteEmpty: [''],
      billEmail: ['', [Validators.required, validationUtils.validateEmail]],
      checkbox: [''],
      companyName: [''],
      datepicker: [''],
      datepickerEmpty: [''],
      email: ['', [Validators.required, validationUtils.validateEmail]],
      firstName: ['', Validators.compose([Validators.required, Validators.minLength(2)])],
      input: [''],
      inputEmpty: [''],
      lastName: ['', Validators.compose([Validators.required, Validators.minLength(2)])],
      phone: [''],
      phoneEmpty: [''],
      radio: [''],
      select: [''],
      selectEmpty: [''],
      textarea: [''],
      textareaEmpty: [''],
    });
  }

  async ngOnInit() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    this.dialogParams = await this.storage.retrieve('dialog-params');
    this.rightSidePanelService.setDialogPageTitle('Components Demo');
    this.loadData();
  }

  @Loading('', true)
  async loadData() {
    try {
      this.rightSidePanelService.setDialogLoading(true);
      this.loaded = false;
      this.settings = await this.getParam('settings');
      const accountType = localStorage.getItem('accountType');
      const userRole = localStorage.getItem('user_role');
      this.showCompanyName = (accountType === 'rental' || accountType === 'company') && userRole !== 'member';
      if (this.settings === null) {
        this.settings = await this.client.getClientSettings();
      }
      this.form.patchValue({
        firstName: this.settings.profile.first_name,
        lastName: this.settings.profile.last_name,
        email: this.settings.profile.email,
        phone: '5551234567',
        companyName: 'Input text',
        billEmail: this.settings.invoices.alt_email !== '' && this.settings.invoices.alt_email ? this.settings.invoices.alt_email : this.settings.profile.email,
        radio: 'radio1',
        textarea: 'Textarea text',
        select: 'select1',
        autocomplete: 'option1',
        datepicker: new Date(),
      });
      this.errorMessage = '';
      this.loaded = true;
      this.rightSidePanelService.setDialogLoading(false);
    } catch (_err) {
      // Silent error handling
    }
  }

  async getParam(paramName): Promise<any> {
    let param = this.dialogParams?.[paramName];
    if (param === null || param === undefined) {
      param = this.navCtrl.getParam(paramName)
    }
    if (param === null || param === undefined) {
      param = await this.storage.retrieve(paramName);
    }
    this.storage.save(paramName, param);
    return param;
  }

  @Loading('', true)
  async save() {
    this.submitted = true;
    if (!this.form.valid) {
      return;
    }
    if (this.form.value.firstName.trim().length < 2 || this.form.value.lastName.trim().length < 2) {
      this.form.patchValue({
        firstName: this.form.value.firstName.trim(),
        lastName: this.form.value.lastName.trim()
      });
      return;
    }
    const payload: any = {
      profile: {
        last_name: this.form.value.lastName.trim(),
        first_name: this.form.value.firstName.trim(),
        phone: parsePhoneNumber(this.countryCode + this.form.value.phone),
        email: this.form.value.email,
        account_name: this.form.value.companyName,
        alt_email: this.form.value.billEmail === '' ? null : this.form.value.billEmail,
      }
    }
    try {
      await this.client.saveClientSettings(payload);
      await this.storage.save('phoneNumber', this.countryCode + this.form.value.phone);
      const updatedSettings = {
        ...this.settings,
        profile: {
          ...this.settings.profile,
          ...payload.profile
        }
      }
      await this.storage.save('settings', updatedSettings);
      await this.me.load(true);
      const params = this.mountSuccessPageParams();
      const url = 'success';
      const component = SuccessPage;
      this.rightSidePanelService.navigateTo(url, params, component);
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
  }

  mountSuccessPageParams() {
    return {
      header: 'Contact Info Saved',
      body: '',
      buttonText: 'Ok',
      buttonRoute: 'more'
    };
  }

  onCountryCodeChange(countryCode: string) {
    this.countryCode = countryCode.replace('+', '');
  }
}
